// Pure slot/link math for the mutual party model (ADR 0038 §2.2). Lives in
// @renavora/shared so BOTH the client (via thin content-aware wrappers in the
// client `party-link.ts`) and the server reuse the identical invariants — this
// is what guarantees in-memory parity by code identity (ADR 0038 §4 ④-b,
// substep 2). No I/O, no time/random, no content dependency: callers inject
// ids, timestamps, and the already-resolved slot cap (the client resolves it
// from the live job tier, the server from the frozen `currentJobTier`).
import { TIER_ALLY_SLOT_CAP } from './jobs.js';
import { MAX_ALLY_SLOTS } from './snapshots.js';
/** Mutual-party slot cap for a job's tier, clamped to MAX_ALLY_SLOTS. */
export function partyLinkCapForTier(tier) {
    return Math.min(MAX_ALLY_SLOTS, TIER_ALLY_SLOT_CAP[tier]);
}
/** Whether a character holding `linkCount` links against `cap` has an open slot. */
export function hasFreePartySlotForCap(linkCount, cap) {
    return linkCount < cap;
}
function formationError(reason, message) {
    return { ok: false, reason, message };
}
function isSameCharacter(a, b) {
    return a.accountId === b.accountId && a.characterId === b.characterId;
}
/**
 * Validate forming a mutual link. Enforces the two core rules: both sides must
 * have an open slot (双方枠消費), and a pair may not be linked twice (the
 * pairwise/non-transitive structure).
 *
 * The reason is POSITION-DEPENDENT: the first argument is treated as the
 * requester, the second as the addressee. Callers MUST always pass
 * `(requesterState, addresseeState)` in that order — regardless of which side
 * initiated the call — so `requester_slot_full` / `addressee_slot_full` stay
 * stable from the wire's point of view (ADR 0038 §4 ④-b substep 2, D4).
 */
export function validatePartyFormationByCap(requester, addressee) {
    if (isSameCharacter(requester, addressee)) {
        return formationError('self_link', 'Cannot form a party link with yourself.');
    }
    if (requester.links.some((link) => isSameCharacter(link.partner, addressee))) {
        return formationError('duplicate_partner', 'You are already linked with this partner.');
    }
    if (addressee.links.some((link) => isSameCharacter(link.partner, requester))) {
        return formationError('duplicate_partner', 'This partner is already linked with you.');
    }
    if (!hasFreePartySlotForCap(requester.links.length, requester.cap)) {
        return formationError('requester_slot_full', 'You have no open party slot at your current Tier.');
    }
    if (!hasFreePartySlotForCap(addressee.links.length, addressee.cap)) {
        return formationError('addressee_slot_full', 'The partner has no open party slot at their current Tier.');
    }
    return { ok: true };
}
/**
 * Turn an approved request into the symmetric pair of links — one stored by the
 * requester (partner = addressee) and one by the addressee (partner = requester).
 */
export function formPartyLinks(request, opts) {
    return {
        requesterLink: { linkId: opts.requesterLinkId, linkedAt: opts.linkedAt, partner: request.addressee },
        addresseeLink: { linkId: opts.addresseeLinkId, linkedAt: opts.linkedAt, partner: request.requester },
    };
}
/** Build a fresh pending request between two members. */
export function createPartyRequest(opts) {
    return {
        id: opts.id,
        status: 'pending',
        createdAt: opts.createdAt,
        requester: opts.requester,
        addressee: opts.addressee,
    };
}
export class PartyRequestTransitionError extends Error {
    constructor(message) {
        super(message);
        this.name = 'PartyRequestTransitionError';
    }
}
/** Move a pending request to `approved`. Throws if it was already resolved. */
export function approvePartyRequest(request, respondedAt) {
    return transition(request, 'approved', respondedAt);
}
/** Move a pending request to `declined`. Throws if it was already resolved. */
export function declinePartyRequest(request, respondedAt) {
    return transition(request, 'declined', respondedAt);
}
function transition(request, status, respondedAt) {
    if (request.status !== 'pending') {
        throw new PartyRequestTransitionError(`Party request ${request.id} is ${request.status}, cannot mark ${status}.`);
    }
    return { ...request, status, respondedAt };
}
/** Append a link (caller validates with validatePartyFormationByCap first). */
export function addPartyLink(links, link) {
    return [...links, link];
}
/** Remove a link by id (no-op when absent). */
export function removePartyLinkById(links, linkId) {
    return links.filter((link) => link.linkId !== linkId);
}
/**
 * Re-freeze a partner's latest snapshot into an existing link, keeping the same
 * linkId and slot position (no re-approval). No-op when the link is absent.
 */
export function refreshPartyLink(links, linkId, freshPartner, refreshedAt) {
    return links.map((link) => link.linkId === linkId ? { ...link, linkedAt: refreshedAt, partner: freshPartner } : link);
}
/** Convert a partner's frozen snapshot into the in-battle ally representation. */
export function partnerToBorrowedAlly(partner, borrowedAt, lockedForRunId) {
    return {
        snapshotId: partner.snapshotId,
        ownerAccountId: partner.accountId,
        borrowedAt,
        ...(lockedForRunId === undefined ? {} : { lockedForRunId }),
        data: partner.data,
        meta: partner.meta,
    };
}
/** Convert every link's partner into in-battle allies for a run. */
export function partyLinksToBorrowedAllies(links, borrowedAt, lockedForRunId) {
    return links.map((link) => partnerToBorrowedAlly(link.partner, borrowedAt, lockedForRunId));
}
//# sourceMappingURL=party-link.js.map